import { Link } from 'react-router-dom';
import type { Project } from '../../lib/projects';
import { PinButton } from './PinButton';
import { StatusBadge } from './StatusBadge';
import { tintFor } from './ProjectThumb';

export function ProjectCard({
  project,
  onTogglePin,
  pinning = false,
}: {
  project: Project;
  /** Omitted where there is nothing to update, e.g. a read-only listing. */
  onTogglePin?: (project: Project) => void;
  pinning?: boolean;
}) {
  return (
    // The pin button has to sit outside the anchor, so the card is a wrapper
    // with the link inside it. `group` moves up here with it.
    <div className="group relative">
      <Link
        to={`/projects/${project.id}`}
        className="flex h-full flex-col overflow-hidden rounded-xl border border-stone-200 bg-white shadow-sm transition hover:-translate-y-0.5 hover:border-stone-300 hover:shadow-md motion-reduce:hover:translate-y-0"
      >
        <div className="aspect-[5/2] w-full overflow-hidden bg-stone-100">
          {project.imageUrl ? (
            <img
              src={project.imageUrl}
              alt=""
              loading="lazy"
              className="h-full w-full object-cover transition group-hover:scale-[1.02]"
            />
          ) : (
            // Most projects have no cover, and a grid of blank grey bands reads
            // as a page of broken images. The same tinted initial the list rows
            // use, at banner size -- keyed off the name, so a project looks
            // like itself in both views.
            <div
              aria-hidden="true"
              className={`flex h-full w-full items-center justify-center text-3xl font-semibold uppercase ${tintFor(
                project.name,
                'banner',
              )}`}
            >
              {project.name.trim()[0] ?? '?'}
            </div>
          )}
        </div>

        <div className="flex flex-1 flex-col p-3">
          <div className="flex items-start justify-between gap-2">
            <h2 className="text-sm font-medium leading-snug tracking-tight text-stone-900">{project.name}</h2>
            <StatusBadge status={project.status} />
          </div>

          {project.description ? (
            <p className="mt-1 line-clamp-2 text-xs leading-relaxed text-stone-600">{project.description}</p>
          ) : null}

          {project.tags.length > 0 ? (
            <ul className="mt-2 flex flex-wrap gap-1">
              {project.tags.map((tag) => (
                <li key={tag} className="rounded-full bg-stone-100 px-1.5 py-0.5 text-[11px] text-stone-600">
                  {tag}
                </li>
              ))}
            </ul>
          ) : null}

          <p className="mt-2 text-[11px] text-stone-500">
            {project.createdBy?.name ?? project.createdBy?.email ?? 'Unknown'} &middot; updated{' '}
            {formatDate(project.updatedAt)}
          </p>
        </div>
      </Link>

      {onTogglePin ? (
        <PinButton
          pinned={project.pinned}
          busy={pinning}
          onClick={() => onTogglePin(project)}
          className="absolute right-2 top-2 bg-white/90 shadow-sm backdrop-blur-sm"
        />
      ) : null}
    </div>
  );
}

export function formatDate(iso: string): string {
  return new Date(iso).toLocaleDateString(undefined, {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
  });
}
